import { PublicClientApplication } from "@azure/msal-browser";
import { loginRequest, silentTokenPolicy } from "../config/msalConfig";
import {
  beginInteractiveRedirect,
  clearRedirectHistory,
  redirectLoopError,
  registerRedirectAttempt,
} from "./interactiveRedirect";
import { logger } from "../logger-init";
import { activePhoneSession, clearPhoneSession, hasPhoneSession } from "./phoneSession";
import { storeOAuthReturnPath } from "./oauthReturnHandler";

// sessionStorage keys for user preferences persistence
const INSTANCE_STORAGE_KEY = "sky-selected-instance-id";
const TENANT_STORAGE_KEY = "sky-selected-tenant-id";
const THEME_STORAGE_KEY = "sky-user-theme";
const LANGUAGE_STORAGE_KEY = "sky-user-language";

/**
 * Auth Service for non-React contexts (utilities, services, etc.)
 *
 * This service provides authentication functionality without React hooks,
 * making it usable in utility files, service files, and other non-component code.
 *
 * Usage:
 *   import { authService } from "../utils/authService";
 *   const token = await authService.getAccessToken();
 */

let msalInstance: PublicClientApplication | null = null;

// Token cache to avoid repeated acquireTokenSilent calls
interface CachedToken {
  accessToken: string;
  expiresAt: number; // timestamp in ms
}
let cachedToken: CachedToken | null = null;
const TOKEN_BUFFER_MS = 60000; // Refresh token 1 minute before expiry

/**
 * Set the MSAL instance (called from index.tsx during initialization)
 */
export function setMsalInstance(instance: PublicClientApplication): void {
  msalInstance = instance;
}

/**
 * Clear the cached token (call on logout or when token becomes invalid)
 */
function clearCachedToken(): void {
  cachedToken = null;
}

/**
 * Get the MSAL instance
 */
function getMsalInstance(): PublicClientApplication {
  if (!msalInstance) {
    throw new Error("MSAL instance not initialized. Call setMsalInstance first.");
  }
  return msalInstance;
}

/**
 * Check if user is logged in
 */
function isLoggedIn(): boolean {
  if (activePhoneSession()) return true;
  const instance = getMsalInstance();
  const accounts = instance.getAllAccounts();
  return accounts.length > 0;
}

/** All keys currently in a Storage (collected first — removing while iterating shifts indexes). */
function storageKeys(storage: Storage): string[] {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key) keys.push(key);
  }
  return keys;
}

/** Remove msal's temporary (per-request) entries from sessionStorage; accounts and tokens live elsewhere. */
function clearMsalTemporaryCache(): void {
  if (typeof sessionStorage === "undefined") return;
  for (const key of storageKeys(sessionStorage)) {
    if (key.startsWith("msal.")) sessionStorage.removeItem(key);
  }
}

/**
 * Ends a phone sign-in and goes to the sign-in page. When the pass simply ran
 * out, the page the person was on is kept so signing in again returns there.
 * Never resolves: the page is navigating away, and callers must not carry on
 * as if a token came back.
 */
function endPhoneSession(returnHere: boolean): Promise<never> {
  clearPhoneSession();
  clearInstanceSelection();
  if (returnHere) {
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== "/" && !currentPath.startsWith("/callback")) {
      storeOAuthReturnPath(currentPath);
    }
  }
  window.location.replace("/");
  return new Promise<never>(() => undefined);
}

/**
 * A kept phone sign-in has run out (or is about to): back to the sign-in page,
 * keeping the page they were on. Never a Microsoft sign-in, which a phone-only
 * user can't complete. Does nothing when there is no phone sign-in.
 */
function phoneSignInEnded(): void {
  if (hasPhoneSession()) void endPhoneSession(true);
}

/** What our API answers when it refuses a bearer token (server/middleware/auth.ts). */
const REFUSED_TOKEN_CODES = new Set(["TOKEN_EXPIRED", "INVALID_TOKEN", "NO_TOKEN"]);

/**
 * Our API refused the phone sign-in pass (it ran out, or the person was
 * blocked or their number changed): back to the sign-in page, keeping the page
 * they were on. Only for those answers from our own API (another service's
 * 401 doesn't end the session), and never for a Microsoft sign-in. Returns
 * whether it ended the session (the page is then going to the sign-in page).
 */
function phonePassRefused(body: unknown): boolean {
  const code = (body as { code?: unknown } | null | undefined)?.code;
  if (hasPhoneSession() && typeof code === "string" && REFUSED_TOKEN_CODES.has(code)) {
    void endPhoneSession(true);
    return true;
  }
  return false;
}

/**
 * Get access token with caching to avoid repeated acquireTokenSilent calls
 *
 * Performance optimization: Caches the token and only calls acquireTokenSilent
 * when the cached token is expired or about to expire (within 1 minute).
 * This eliminates 400-600ms delays per API call during initialization.
 */
async function getAccessToken(): Promise<string> {
  // A phone sign-in has one pass and no silent refresh: use it while it lasts,
  // then send the person back to the sign-in page.
  const phone = activePhoneSession();
  if (phone) return phone.token;
  if (hasPhoneSession()) return endPhoneSession(true);

  // Check if we have a valid cached token
  if (cachedToken && Date.now() < cachedToken.expiresAt - TOKEN_BUFFER_MS) {
    return cachedToken.accessToken;
  }

  const instance = getMsalInstance();
  const accounts = instance.getAllAccounts();

  if (accounts.length === 0) {
    throw new Error("No authenticated user found");
  }

  try {
    // Try silent token acquisition first (cache / refresh token only — no iframe)
    const response = await instance.acquireTokenSilent({
      ...loginRequest,
      ...silentTokenPolicy,
      account: accounts[0],
    });

    // Cache the token with expiry time
    if (response.expiresOn) {
      cachedToken = {
        accessToken: response.accessToken,
        expiresAt: response.expiresOn.getTime(),
      };
    }
    // A token in hand means any earlier interactive redirects were not a loop.
    clearRedirectHistory();

    return response.accessToken;
  } catch (error: unknown) {
    // Clear cached token on error
    cachedToken = null;

    // Handle specific MSAL errors
    const msalError = error as { errorCode?: string };
    if (msalError?.errorCode === "no_token_request_cache_error") {
      logger.warn("Token request cache error detected, clearing MSAL's temporary cache...");
      clearMsalTemporaryCache();
    }

    // If silent acquisition fails, trigger interactive login
    // Use popup for iframes, redirect for top-level windows
    if (isInIframe()) {
      try {
        const response = await instance.acquireTokenPopup(loginRequest);
        if (response.expiresOn) {
          cachedToken = {
            accessToken: response.accessToken,
            expiresAt: response.expiresOn.getTime(),
          };
        }
        return response.accessToken;
      } catch (popupError) {
        logger.error(
          "Popup token acquisition failed",
          popupError instanceof Error ? popupError : undefined,
        );
        throw new Error("Failed to acquire token. Please try again.");
      }
    } else {
      // Shared, single-flight redirect: concurrent callers (app-init fires
      // several requests at once) must never each start their own — see
      // interactiveRedirect.ts for why that loops forever. Never resolves.
      return beginInteractiveRedirect(instance, loginRequest);
    }
  }
}

/**
 * Get authorization header for API calls
 */
export async function getAuthorizationHeader(): Promise<{
  "Authorization": string;
  "InstanceId": string;
  "Accept": string;
  "Content-Type": string;
}> {
  const token = await getAccessToken();
  const instanceId = getInstanceId();
  return {
    "Authorization": `Bearer ${token}`,
    "InstanceId": instanceId || "",
    "Accept": "application/json",
    "Content-Type": "application/json",
  };
}

/**
 * Get authorization header for multipart requests
 */
async function getMultiPartAuthorizationHeader(): Promise<{
  Authorization: string;
  InstanceId: string;
  Accept: string;
}> {
  return getAuthorizationHeader();
}

/**
 * Get combined headers (authorization + content-type)
 */
async function getHeader(): Promise<{
  "Authorization": string;
  "Content-Type": string;
  "InstanceId": string;
  "Accept": string;
}> {
  const token = await getAccessToken();
  const instanceId = getInstanceId();
  return {
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json",
    "InstanceId": instanceId || "",
    "Accept": "application/json",
  };
}

/**
 * Get instance ID from URL query string or sessionStorage
 */
export function getInstanceId(): string {
  // First check URL query string
  const urlParams = new URLSearchParams(window.location.search);
  const urlInstanceId = urlParams.get("instanceid") || urlParams.get("instanceId");
  if (urlInstanceId) {
    return urlInstanceId;
  }

  // Fallback to sessionStorage
  return sessionStorage.getItem(INSTANCE_STORAGE_KEY) || "";
}

/**
 * Get query string with instance ID
 */
function getQueryString(): string {
  const instanceId = getInstanceId();
  return instanceId ? `?instanceid=${instanceId}` : "";
}

/**
 * Get saved tenant ID from sessionStorage
 */
export function getSavedTenantId(): string {
  return sessionStorage.getItem(TENANT_STORAGE_KEY) || "";
}

/**
 * Get saved instance ID from sessionStorage
 */
export function getSavedInstanceId(): string {
  return sessionStorage.getItem(INSTANCE_STORAGE_KEY) || "";
}

/**
 * Save tenant ID to sessionStorage
 */
function saveTenantId(tenantId: string): void {
  if (tenantId) {
    sessionStorage.setItem(TENANT_STORAGE_KEY, tenantId);
  }
}

/**
 * Save instance ID to sessionStorage
 */
function saveInstanceId(instanceId: string): void {
  if (instanceId) {
    sessionStorage.setItem(INSTANCE_STORAGE_KEY, instanceId);
  }
}

/**
 * Get saved theme from localStorage (persists across tabs and browser restarts)
 */
function getSavedTheme(): string {
  return localStorage.getItem(THEME_STORAGE_KEY) || "Light";
}

/**
 * Save theme to localStorage (persists across tabs and browser restarts)
 */
function saveTheme(theme: string): void {
  if (theme) {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }
}

/**
 * Get saved language from sessionStorage
 */
function getSavedLanguage(): string {
  return sessionStorage.getItem(LANGUAGE_STORAGE_KEY) || "English";
}

/**
 * Save language to sessionStorage
 */
function saveLanguage(language: string): void {
  if (language) {
    sessionStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }
}

/**
 * Get current user information
 */
function currentUser(): any {
  const phone = activePhoneSession();
  if (phone) {
    return {
      id: phone.userId,
      name: phone.name,
      email: phone.email,
      roles: [],
      isAccountEnabled: true,
    };
  }
  const instance = getMsalInstance();
  const accounts = instance.getAllAccounts();

  if (accounts.length === 0) {
    return null;
  }

  const account = accounts[0];
  const idTokenClaims = account.idTokenClaims as any;

  return {
    id: account.localAccountId,
    name: account.name || idTokenClaims?.name || "",
    email: account.username || idTokenClaims?.email || "",
    roles: idTokenClaims?.roles || [],
    isAccountEnabled: idTokenClaims?.isAccountEnabled !== false,
  };
}

/**
 * Clear all user preferences from storage
 * Called on logout to prevent cross-user data leakage
 */
function clearInstanceSelection(): void {
  // Clear cached token
  clearCachedToken();

  // Drop every piece of app session state, but NOT msal's temporary cache
  // (`msal.*`: PKCE verifier, request state, interaction flag). A wholesale
  // sessionStorage.clear() here used to run while a redirect to B2C was
  // being prepared, which made the returned auth code unredeemable.
  // Theme stays in localStorage so it persists across login/logout sessions.
  if (typeof sessionStorage !== "undefined") {
    for (const key of storageKeys(sessionStorage)) {
      if (!key.startsWith("msal.")) sessionStorage.removeItem(key);
    }
  }
}

/**
 * Detect if the app is running inside an iframe
 */
function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // If we can't access window.top due to cross-origin, we're in an iframe
    return true;
  }
}

/**
 * Clear ALL MSAL state from session and local storage
 * This aggressively fixes the "interaction_in_progress" error by removing
 * all MSAL-related keys, not just interaction keys
 */
function clearAllMsalState(): void {
  const clearMsalKeys = (storage: Storage) => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith("msal.")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => {
      logger.debug(`Removing MSAL key: ${key}`);
      storage.removeItem(key);
    });
    return keysToRemove.length;
  };

  let totalRemoved = 0;
  if (typeof sessionStorage !== "undefined") {
    totalRemoved += clearMsalKeys(sessionStorage);
  }
  if (typeof sessionStorage !== "undefined") {
    totalRemoved += clearMsalKeys(sessionStorage);
  }
  logger.debug(`Cleared ${totalRemoved} MSAL keys from storage`);
}

/** How long signing out waits for the server before going on regardless. */
const SIGN_OUT_WAIT_MS = 5000;

/**
 * Ends the phone pass on the server too (on every device), so a copied pass
 * stops working. Best effort: signing out in this browser never waits on it
 * for long or fails because of it.
 */
async function endPhoneSignInOnServer(): Promise<void> {
  const token = activePhoneSession()?.token;
  if (!token) return;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SIGN_OUT_WAIT_MS);
  try {
    await fetch("/api/v1/auth/phone/sign-out", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
  } catch {
    // Offline or slow: the pass still runs out on its own.
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Logout user
 * Uses popup for iframes, redirect for top-level windows
 */
async function logout(): Promise<void> {
  if (hasPhoneSession()) {
    await endPhoneSignInOnServer();
    await endPhoneSession(false);
    return;
  }
  const instance = getMsalInstance();

  // Clear instance selection
  clearInstanceSelection();

  // Use popup for iframes, redirect otherwise
  if (isInIframe()) {
    await instance.logoutPopup();
  } else {
    await instance.logoutRedirect();
  }
}

/**
 * Login with automatic method selection
 * - Uses popup for iframes (redirect doesn't work in iframes due to security restrictions)
 * - Uses redirect for top-level windows (better UX)
 * - Falls back to popup if redirect fails with interaction_in_progress
 */
async function login(domainHint?: string): Promise<void> {
  // A provider picked on the sign-in page goes straight to it (B2C domain_hint);
  // with none, B2C shows its own picker.
  const request = domainHint
    ? { ...loginRequest, extraQueryParameters: { domain_hint: domainHint } }
    : loginRequest;
  const instance = getMsalInstance();

  // If we're in an iframe, use popup (redirect won't work)
  if (isInIframe()) {
    logger.info("Detected iframe context, using popup login...");
    try {
      const response = await instance.loginPopup(request);
      if (response) {
        logger.info("Popup login successful");
      }
    } catch (error: unknown) {
      const authError = error as { errorCode?: string };
      if (authError?.errorCode === "interaction_in_progress") {
        logger.warn("Clearing MSAL state and retrying popup login...");
        clearAllMsalState();
        await new Promise((resolve) => setTimeout(resolve, 500));
        const response = await instance.loginPopup(request);
        if (response) {
          logger.info("Popup login successful after state clear");
        }
      } else if (
        authError?.errorCode === "popup_window_error" ||
        authError?.errorCode === "user_cancelled"
      ) {
        logger.warn("Popup blocked or cancelled by user");
        throw error;
      } else {
        throw error;
      }
    }
    return;
  }

  // For top-level windows, try redirect first, fall back to popup
  try {
    // Ensure any pending interaction is resolved before starting a new one.
    // MSAL v4 tracks interaction state in-memory, so clearing storage alone
    // does not help — handleRedirectPromise() is the only way to properly
    // drain the in-memory queue.
    // Same breaker as the token-recovery redirect: a B2C round trip that keeps
    // coming back unauthenticated must not be retried forever.
    if (!registerRedirectAttempt()) {
      logger.error("Sign-in redirect loop detected on the login path; not redirecting again");
      throw redirectLoopError();
    }
    await instance.handleRedirectPromise();
    await instance.loginRedirect(request);
  } catch (error: unknown) {
    const authError = error as { errorCode?: string };
    if (authError?.errorCode === "interaction_in_progress") {
      logger.warn("Redirect login failed with interaction_in_progress, trying popup fallback...");

      // Try popup as fallback — popup uses a new window so it bypasses
      // the in-memory interaction lock on the main frame.
      try {
        const response = await instance.loginPopup(request);
        if (response) {
          logger.info("Popup login successful after redirect failure");
        }
      } catch (popupError: unknown) {
        const popupAuthError = popupError as { errorCode?: string };
        if (
          popupAuthError?.errorCode === "popup_window_error" ||
          popupAuthError?.errorCode === "interaction_in_progress"
        ) {
          // Popups blocked or still conflicted — tell user to reload
          const recoveryError = new Error("Please reload the page to sign in.");
          (recoveryError as { errorCode?: string }).errorCode = "popup_blocked_reload_required";
          throw recoveryError;
        }
        throw popupError;
      }
    } else {
      throw error;
    }
  }
}

/**
 * Auth service object (for backward compatibility)
 */
export const authService = {
  setMsalInstance,
  isLoggedIn,
  isInIframe,
  getAccessToken,
  getAuthorizationHeader,
  getMultiPartAuthorizationHeader,
  getHeader,
  getInstanceId,
  getQueryString,
  getSavedTenantId,
  getSavedInstanceId,
  saveTenantId,
  saveInstanceId,
  getSavedTheme,
  saveTheme,
  getSavedLanguage,
  saveLanguage,
  currentUser,
  logout,
  login,
  clearInstanceSelection,
  clearCachedToken,
  phonePassRefused,
  phoneSignInEnded,

  // Getter for MSAL instance (for advanced use cases)
  get msalInstance() {
    return getMsalInstance();
  },
};
